import React, { useState, useEffect, useRef } from 'react';
import { 
    Table, TableBody, Grid, TableCell, TableContainer, TableRow, Paper, TableHead, Button, 
    TablePagination, TextField, Dialog, DialogActions, DialogContent, DialogContentText, 
    DialogTitle, Typography, IconButton, Chip, Tooltip, Box, CircularProgress, Alert, Autocomplete 
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    Work as WorkIcon,
    LocalHospital as TarjetaSaludIcon,
    Bloodtype as TipoSangreIcon
} from '@mui/icons-material';
import GestionClientes from './GestionClientes';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import GestionOrdenTrabajo from '../Cotizaciones/GestionOrdenTrabajo'; // Ajusta la ruta si es necesario
function Clientes() {
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;
    const key = process.env.REACT_APP_API_KEY;
    const [clientes, setClientes] = useState([]);
    const [totalClientes, setTotalClientes] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [editCliente, setEditCliente] = useState(null); // Nuevo estado para manejar la edición
    const [openDialog, setOpenDialog] = useState(false); // Estado para manejar el diálogo de confirmación de eliminación
    const [clienteToDelete, setClienteToDelete] = useState(null); // Cliente a eliminar
    const { user } = useUser();
    
    // Validar permisos
    const puedeEditar = tienePermiso('Editar Clientes');
    const puedeEliminar = tienePermiso('Eliminar Cliente');
    const puedeTrasladar = tienePermiso('Trasladar Cliente');
    const puedeCrearOrden = tienePermiso('Crear Orden desde Cliente');
    const puedeImprimirSalud = tienePermiso('Imprimir Tarjeta Salud');
    const puedeImprimirSangre = tienePermiso('Imprimir Tipo Sangre');
    const [openOrdenTrabajoModal, setOpenOrdenTrabajoModal] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const gestionClientesRef = useRef(null);
    
    // Estados para la previsualización de PDFs
    const [openPdfPreview, setOpenPdfPreview] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const [pdfTitle, setPdfTitle] = useState('');

    // Estados para trasladar cliente
    const [openTrasladarDialog, setOpenTrasladarDialog] = useState(false);
    const [clienteOrigen, setClienteOrigen] = useState(null);
    const [clienteDestino, setClienteDestino] = useState(null);
    const [searchClienteOrigen, setSearchClienteOrigen] = useState('');
    const [searchClienteDestino, setSearchClienteDestino] = useState('');
    const [clientesOrigenOpciones, setClientesOrigenOpciones] = useState([]);
    const [clientesDestinoOpciones, setClientesDestinoOpciones] = useState([]);
    const [loadingBusqueda, setLoadingBusqueda] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false); // loading for main search (button/enter)
    const [searchError, setSearchError] = useState(null);
    const [noResultsMessage, setNoResultsMessage] = useState(null);
    
    // Referencias para el debounce de búsqueda
    const debounceTimerOrigen = useRef(null);
    const debounceTimerDestino = useRef(null);

    // Listener para cerrar el modal de Orden de Trabajo cuando se dispara desde GestionFactura
    useEffect(() => {
        const handleCloseOrdenTrabajoModal = () => {
            setOpenOrdenTrabajoModal(false);
        };

        // Escuchar el evento personalizado
        window.addEventListener('closeOrdenTrabajoModal', handleCloseOrdenTrabajoModal);

        // Limpieza al desmontar
        return () => {
            window.removeEventListener('closeOrdenTrabajoModal', handleCloseOrdenTrabajoModal);
            // Limpiar los timers pendientes al desmontar el componente
            if (debounceTimerOrigen.current) {
                clearTimeout(debounceTimerOrigen.current);
            }
            if (debounceTimerDestino.current) {
                clearTimeout(debounceTimerDestino.current);
            }
        };
    }, []);

    // Función para cargar los clientes
    const fetchClientes = async (page = 0, limit = 100, search = '') => {
        try {
            const bodyData = {
                page: page + 1,
                limit: limit,
                search: search
            };
            console.log(bodyData);
            console.log(`${server}/MgClientes/ClientePaginados`);
            const response = await fetch(`${server}/MgClientes/ClientePaginados`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(bodyData)
            });
            console.log(response);
            const data = await response.json();
            console.log(data);
            if (data.clientes.$values.length < limit) {
                setHasMore(false); // No hay más clientes por cargar
            }
            // En lugar de reemplazar, concatenamos los nuevos clientes
            setClientes((prevClientes) => [...prevClientes, ...data.clientes.$values]);
            setTotalClientes(data.total);
            return data; // return data so callers can inspect results
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los clientes: ' + error,
            });
            // Return null so callers know an error happened
            return null;
        }
    };


    // Use effect para cargar los clientes iniciales
    useEffect(() => {
        fetchClientes(0);
    }, []);

    // Use effect para cargar más clientes al acercarse al final de la paginación
    useEffect(() => {
        if (page * rowsPerPage >= clientes.length - rowsPerPage && hasMore && !isLoadingMore) {
            setIsLoadingMore(true);
            fetchClientes(page, 100, searchQuery).then(() => setIsLoadingMore(false));
        }
    }, [page]);

    // const handleSearchChange = async (event) => {
    //     const query = event.target.value.toLowerCase();
    //     setSearchQuery(query);
    //     setPage(0);
    //     setClientes([]);
    //     setHasMore(true);
    //     await fetchClientes(0, 100, query); // Realizar la búsqueda en el servidor
    // };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setSearchError(null);
        setNoResultsMessage(null);
    };

    const handleSearch = async () => {
        setPage(0);
        setClientes([]);
        setHasMore(true);
        setLoadingSearch(true);
        setSearchError(null);
        setNoResultsMessage(null);

        try {
            const data = await fetchClientes(0, 100, searchQuery);

            if (!data) {
                setSearchError('Error al obtener resultados del servidor. Intente nuevamente.');
                return;
            }
            // handle empty results
            const results = data && data.clientes && data.clientes.$values ? data.clientes.$values : [];
            if (results.length === 0) {
                setNoResultsMessage('No se encontraron clientes para la búsqueda.');
            }
        } catch (err) {
            console.error('Error al buscar clientes:', err);
            setSearchError('Error al buscar clientes: ' + (err.message || err));
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al buscar clientes: ' + (err.message || err),
            });
        } finally {
            setLoadingSearch(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reinicia la paginación al cambiar el número de filas por página
    };

    const handleEditCliente = (cliente) => {
        if (!puedeEditar) {
            Swal.fire({
                icon: 'error',
                title: 'Acceso denegado',
                text: 'No tienes permiso para editar clientes'
            });
            return;
        }
        
        setEditCliente(cliente);
        // Scroll automático solo para editar (para visualizar los campos del formulario)
        setTimeout(() => {
            if (gestionClientesRef.current) {
                gestionClientesRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 100);
    };

    const handleNewCliente = async () => {
        // Solo resetea el modo de edición
        // La función onRefreshClientesList ya se encarga de actualizar la lista
        setEditCliente(null);
    };

    const handleRefreshClientesList = async () => {
        // Reinicia la paginación y limpia la lista actual
        setPage(0);                  
        setClientes([]);            
        setHasMore(true);           
        // Recarga los datos de forma inmediata
        try {
            await fetchClientes(0, 100, searchQuery);
        } catch (error) {
            console.error('Error al refrescar la lista de clientes:', error);
        }
    };

    const handleOpenDialog = (cliente) => {
        if (!puedeEliminar) {
            Swal.fire({
                icon: 'error',
                title: 'Acceso denegado',
                text: 'No tienes permiso para eliminar clientes'
            });
            return;
        }
        
        setClienteToDelete(cliente);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setClienteToDelete(null);
    };

    const handleDeleteCliente = async () => {
        if (clienteToDelete) {
            try {
                const username = '' + user.codigoUsuario + '/';

                const response = await fetch(`${server}/MgClientes/${clienteToDelete.idCliente}/${username}`, {
                    method: 'DELETE'
                });
                
                if (response.ok) {
                    handleCloseDialog();
                    // Resetear la lista y recargar desde el principio
                    setPage(0);
                    setClientes([]);
                    setHasMore(true);
                    await fetchClientes(0, 100, searchQuery);
                    Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: 'Cliente eliminado con éxito',
                    });
                } else {
                    handleCloseDialog();

                    const errorData = await response.json();
                    const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                    Swal.fire({
                        icon: 'error',
                        title: `Error ${response.status}`,
                        text: errorMessage,
                    });
                }
            } catch (error) {
                handleCloseDialog();
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error al eliminar el Cliente: ' + error,
                });
            }
        }
    };


    const handleOpenOrdenTrabajo = (cliente) => {
        setClienteSeleccionado(cliente);
        setOpenOrdenTrabajoModal(true);
    };

    // Funciones auxiliares para verificar si los datos están completos
    const isRprDataComplete = (cliente) => {
        return cliente && cliente.rpr && cliente.fechaRpr;
    };

    const isTipoRhDataComplete = (cliente) => {
        return cliente && cliente.idTipoRh && cliente.fechaTipoRh;
    };

    const getRprTooltip = (cliente) => {
        if (isRprDataComplete(cliente)) {
            return "Imprimir Tarjeta de Salud";
        }
        const missing = [];
        if (!cliente?.rpr) missing.push("RPR");
        if (!cliente?.fechaRpr) missing.push("Fecha RPR");
        return `Faltan datos: ${missing.join(", ")}`;
    };

    const getTipoRhTooltip = (cliente) => {
        if (isTipoRhDataComplete(cliente)) {
            return "Imprimir Tipo de Sangre";
        }
        const missing = [];
        if (!cliente?.idTipoRh) missing.push("Tipo RH");
        if (!cliente?.fechaTipoRh) missing.push("Fecha Tipo RH");
        return `Faltan datos: ${missing.join(", ")}`;
    };

    // Función para imprimir la Tarjeta de Salud
    const handleImprimirTarjetaSalud = async (cliente) => {
        if (!cliente) return;
        
        setPdfLoading(true);
        setPdfError(null);
        setPdfTitle(`Tarjeta de Salud - ${cliente.nombreCompleto}`);
        
        try {
            const response = await fetch(`${server}/MgClientes/ImprimirTarjetaSalud/${cliente.idCliente}`, {
                method: 'POST',
                headers: {
                    'accept': '*/*'
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                setPdfUrl(url);
                setOpenPdfPreview(true);
            } else {
                setPdfError('Error al generar la Tarjeta de Salud');
                console.error('Error al imprimir la Tarjeta de Salud');
            }
        } catch (error) {
            setPdfError('Hubo un error al imprimir la Tarjeta de Salud');
            console.error('Error al imprimir la Tarjeta de Salud:', error);
        } finally {
            setPdfLoading(false);
        }
    };

    // Función para imprimir el Tipo de Sangre
    const handleImprimirTipoSangre = async (cliente) => {
        if (!cliente) return;
        
        setPdfLoading(true);
        setPdfError(null);
        setPdfTitle(`Tipo de Sangre - ${cliente.nombreCompleto}`);
        
        try {
            const response = await fetch(`${server}/MgClientes/ImprimirTipoSangre/${cliente.idCliente}`, {
                method: 'POST',
                headers: {
                    'accept': '*/*'
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                setPdfUrl(url);
                setOpenPdfPreview(true);
            } else {
                setPdfError('Error al generar el Tipo de Sangre');
                console.error('Error al imprimir el Tipo de Sangre');
            }
        } catch (error) {
            setPdfError('Hubo un error al imprimir el Tipo de Sangre');
            console.error('Error al imprimir el Tipo de Sangre:', error);
        } finally {
            setPdfLoading(false);
        }
    };

    // Función para cerrar el modal de PDF
    const handleClosePdfPreview = () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        setPdfError(null);
        setOpenPdfPreview(false);
    };

    // Función para buscar clientes para trasladar (se ejecuta directamente sin debounce)
    const buscarClientesParaTrasladar = async (busqueda, tipo) => {
        if (!busqueda || busqueda.length < 3) {
            if (tipo === 'origen') setClientesOrigenOpciones([]);
            if (tipo === 'destino') setClientesDestinoOpciones([]);
            return;
        }

        setLoadingBusqueda(true);
        try {
            // Usar el mismo endpoint de paginado que ya funciona bien
            const bodyData = {
                page: 1,
                limit: 50, // Limitar a 50 resultados para el autocomplete
                search: busqueda
            };

            const response = await fetch(`${server}/MgClientes/ClientePaginados`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(bodyData)
            });

            const data = await response.json();
            
            if (response.ok && data.clientes && data.clientes.$values) {
                if (tipo === 'origen') {
                    setClientesOrigenOpciones(data.clientes.$values);
                } else {
                    setClientesDestinoOpciones(data.clientes.$values);
                }
            }
        } catch (error) {
            console.error('Error al buscar clientes:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al buscar clientes: ' + error.message,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setLoadingBusqueda(false);
        }
    };

    // Función con debounce para búsqueda de cliente origen
    const handleBusquedaOrigenDebounced = (valor) => {
        // Limpiar el timer anterior
        if (debounceTimerOrigen.current) {
            clearTimeout(debounceTimerOrigen.current);
        }

        // Si hay menos de 3 caracteres, limpiar opciones inmediatamente
        if (!valor || valor.length < 3) {
            setClientesOrigenOpciones([]);
            return;
        }

        // Establecer nuevo timer de 1 segundo
        debounceTimerOrigen.current = setTimeout(() => {
            buscarClientesParaTrasladar(valor, 'origen');
        }, 1000);
    };

    // Función con debounce para búsqueda de cliente destino
    const handleBusquedaDestinoDebounced = (valor) => {
        // Limpiar el timer anterior
        if (debounceTimerDestino.current) {
            clearTimeout(debounceTimerDestino.current);
        }

        // Si hay menos de 3 caracteres, limpiar opciones inmediatamente
        if (!valor || valor.length < 3) {
            setClientesDestinoOpciones([]);
            return;
        }

        // Establecer nuevo timer de 1 segundo
        debounceTimerDestino.current = setTimeout(() => {
            buscarClientesParaTrasladar(valor, 'destino');
        }, 1000);
    };

    // Función para abrir el modal de trasladar
    const handleOpenTrasladarDialog = () => {
        setOpenTrasladarDialog(true);
        setClienteOrigen(null);
        setClienteDestino(null);
        setSearchClienteOrigen('');
        setSearchClienteDestino('');
        setClientesOrigenOpciones([]);
        setClientesDestinoOpciones([]);
    };

    // Función para cerrar el modal de trasladar
    const handleCloseTrasladarDialog = () => {
        // Limpiar los timers pendientes
        if (debounceTimerOrigen.current) {
            clearTimeout(debounceTimerOrigen.current);
        }
        if (debounceTimerDestino.current) {
            clearTimeout(debounceTimerDestino.current);
        }
        
        setOpenTrasladarDialog(false);
        setClienteOrigen(null);
        setClienteDestino(null);
        setSearchClienteOrigen('');
        setSearchClienteDestino('');
        setClientesOrigenOpciones([]);
        setClientesDestinoOpciones([]);
    };

    // Función principal para trasladar cliente
    const handleTrasladarCliente = async () => {
        if (!clienteOrigen || !clienteDestino) {
            Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Debe seleccionar tanto el cliente origen como el cliente destino.'
            });
            return;
        }

        if (clienteOrigen.idCliente === clienteDestino.idCliente) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'El cliente origen y destino no pueden ser el mismo.'
            });
            return;
        }

        // Confirmación antes de trasladar
        const confirmacion = await Swal.fire({
            icon: 'warning',
            title: '¿Está seguro?',
            html: `
                <p><strong>Esta acción es irreversible.</strong></p>
                <p>Se trasladarán todos los datos de:</p>
                <p><strong>Cliente Origen:</strong><br/>${clienteOrigen.nombreCompleto}<br/>ID: ${clienteOrigen.numeroIdentidad}</p>
                <p>⬇️</p>
                <p><strong>Cliente Destino:</strong><br/>${clienteDestino.nombreCompleto}<br/>ID: ${clienteDestino.numeroIdentidad}</p>
                <p>El cliente origen será marcado como eliminado.</p>
            `,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, trasladar',
            cancelButtonText: 'Cancelar'
        });

        if (!confirmacion.isConfirmed) return;

        try {
            const response = await fetch(`${server}/MgClientes/TrasladarCliente`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idClienteOrigen: clienteOrigen.idCliente,
                    idClienteDestino: clienteDestino.idCliente,
                    modificadoPor: user?.codigoUsuario || 'ADMIN'
                })
            });

            const data = await response.json();

            if (response.ok) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Traslado exitoso',
                    html: `
                        <p>${data.mensaje}</p>
                        <p><strong>Registros afectados:</strong> ${data.registrosAfectados || 0}</p>
                    `
                });
                
                // Cerrar el modal y refrescar la lista
                handleCloseTrasladarDialog();
                await handleRefreshClientesList();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al trasladar',
                    text: data.mensaje || 'No se pudo realizar el traslado.'
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error de conexión',
                text: 'No se pudo conectar con el servidor: ' + error.message
            });
        }
    };

    return (
        <>
            <div className="right_col" role="main">
                <div className="x_title"></div>
                <div className="x_content">

                    {/* <div className="row"> */}

                    {/* <div className="col-md-8 col-sm-4 offset-md-2"> */}
                    <div className="ln_solid" />
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h2>Clientes Existentes</h2>
                        <Button 
                            variant="contained" 
                            color="secondary" 
                            onClick={handleOpenTrasladarDialog}
                            disabled={!puedeTrasladar}
                        >
                            Trasladar Cliente
                        </Button>
                    </Box>
                    <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
                        <Grid item xs={10}>
                            <TextField
                                variant="outlined"
                                label="Buscar cliente"
                                fullWidth
                                disabled={loadingSearch}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        loadingSearch ? (
                                            <InputAdornment position="end">
                                                <CircularProgress size={18} />
                                            </InputAdornment>
                                        ) : null
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Button variant="contained" color="primary" onClick={handleSearch} disabled={loadingSearch}>
                                    Buscar
                                </Button>
                                {loadingSearch && <CircularProgress size={24} />}
                            </Box>
                        </Grid>
                    </Grid>
                        {/* Mensajes de resultado de búsqueda */}
                        {searchError && (
                            <Box mb={2}>
                                <Alert severity="error">{searchError}</Alert>
                            </Box>
                        )}

                        {noResultsMessage && (
                            <Box mb={2}>
                                <Alert severity="info">{noResultsMessage}</Alert>
                            </Box>
                        )}

                        <div className="table-responsive">
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="right">Acciones</TableCell>
                                        <TableCell>Número de Identidad</TableCell>
                                        <TableCell style={{ minWidth: '200px' }}>Nombre Completo</TableCell>
                                        <TableCell>Correo Electrónico</TableCell>
                                        <TableCell>Teléfono</TableCell>
                                        <TableCell>Celular</TableCell>
                                        <TableCell>Sexo</TableCell>
                                        <TableCell>Fecha de Nacimiento</TableCell>
                                        <TableCell>Estado Civil</TableCell>
                                        <TableCell>Tipo RH</TableCell>
                                        <TableCell>Barrio o Colonia</TableCell>
                                        <TableCell>Etnia</TableCell>
                                        <TableCell>Raza</TableCell>
                                        <TableCell>Profesión</TableCell>
                                        <TableCell>Cont. de Emergencia</TableCell>
                                        <TableCell>Teléfono Cont.</TableCell>
                                        <TableCell>Parentesco</TableCell>
                                        <TableCell>Tarjeta Descuento</TableCell>
                                        <TableCell>Whatsapp</TableCell>
                                        <TableCell>Pasaporte</TableCell>
                                        <TableCell>Poliza</TableCell>
                                        <TableCell>Cuenta Contable</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cliente) => (
                                        <TableRow key={cliente.idCliente}>
                                            <TableCell align="right" style={{ minWidth: '200px', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={!puedeEditar ? "No tienes permiso para editar" : "Editar cliente"}>
                                                    <span>
                                                        <IconButton 
                                                            color="primary" 
                                                            onClick={() => handleEditCliente(cliente)}
                                                            size="small"
                                                            style={{ padding: '4px' }}
                                                            disabled={!puedeEditar}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                
                                                <Tooltip title={!puedeCrearOrden ? "No tienes permiso" : "Orden de Trabajo"}>
                                                    <span>
                                                    <IconButton 
                                                        color="primary" 
                                                        onClick={() => handleOpenOrdenTrabajo(cliente)}
                                                        size="small"
                                                        style={{ padding: '4px' }}
                                                        disabled={!puedeCrearOrden}
                                                    >
                                                        <WorkIcon fontSize="small" />
                                                    </IconButton>
                                                    </span>
                                                </Tooltip>
                                                
                                                <Tooltip title={!puedeImprimirSalud ? "No tienes permiso" : getRprTooltip(cliente)}>
                                                    <span>
                                                        <IconButton 
                                                            color="success" 
                                                            onClick={() => handleImprimirTarjetaSalud(cliente)}
                                                            size="small"
                                                            disabled={pdfLoading || !isRprDataComplete(cliente) || !puedeImprimirSalud}
                                                            style={{ padding: '4px' }}
                                                        >
                                                            <TarjetaSaludIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                
                                                <Tooltip title={!puedeImprimirSangre ? "No tienes permiso" : getTipoRhTooltip(cliente)}>
                                                    <span>
                                                        <IconButton 
                                                            color="error" 
                                                            onClick={() => handleImprimirTipoSangre(cliente)}
                                                            size="small"
                                                            disabled={pdfLoading || !isTipoRhDataComplete(cliente) || !puedeImprimirSangre}
                                                            style={{ padding: '4px' }}
                                                        >
                                                            <TipoSangreIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                
                                                <Tooltip title={!puedeEliminar ? "No tienes permiso para eliminar" : "Eliminar cliente"}>
                                                    <span>
                                                        <IconButton 
                                                            color="secondary" 
                                                            onClick={() => handleOpenDialog(cliente)}
                                                            size="small"
                                                            style={{ padding: '4px' }}
                                                            disabled={!puedeEliminar}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{cliente.numeroIdentidad}</TableCell>
                                            <TableCell style={{ minWidth: '200px' }}>{cliente.nombreCompleto}</TableCell>
                                            <TableCell>{cliente.correoElectronico}</TableCell>
                                            <TableCell>{cliente.telefono}</TableCell>
                                            <TableCell>{cliente.celular}</TableCell>
                                            <TableCell>{cliente.descripcionSexo}</TableCell>
                                            <TableCell>{cliente.fechaNacimiento}</TableCell>
                                            <TableCell>{cliente.estadoCivil}</TableCell>
                                            <TableCell>{cliente.descripcionTipoRh}</TableCell>
                                            <TableCell>{cliente.nombreBarrioColonia}</TableCell>
                                            <TableCell>{cliente.descripcionEtnia}</TableCell>
                                            <TableCell>{cliente.descripcionRaza}</TableCell>
                                            <TableCell>{cliente.descripcionProfesion}</TableCell>
                                            <TableCell>{cliente.contactoDeEmergencia}</TableCell>
                                            <TableCell>{cliente.telefonoContacto}</TableCell>
                                            <TableCell>{cliente.descripcionParentesco}</TableCell>
                                            <TableCell>{cliente.descripcionTarjeta}</TableCell>
                                            <TableCell>{cliente.whatsapp ? 'Sí' : 'No'}</TableCell>
                                            <TableCell>{cliente.pasaporte}</TableCell>
                                            <TableCell>{cliente.descripcionPoliza}</TableCell>
                                            <TableCell>{cliente.descripcionCuentaContable}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={totalClientes}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Filas por página:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                            />
                        </TableContainer>

                        <Dialog
                            open={openOrdenTrabajoModal}
                            onClose={(event, reason) => {
                                if (reason === 'backdropClick') return; // Evita el cierre al hacer click afuera
                                setOpenOrdenTrabajoModal(false);
                                setClienteSeleccionado(null); // Limpia el cliente seleccionado si es necesario
                            }}
                            fullWidth
                            maxWidth="100%"
                        >
                            <DialogTitle>Orden de Trabajo</DialogTitle>
                            <DialogContent>
                                <GestionOrdenTrabajo
                                    cliente={clienteSeleccionado}
                                    onClose={(reason) => {
                                        setOpenOrdenTrabajoModal(false);
                                        setClienteSeleccionado(null);
                                        if (reason === 'closeAll') {
                                            window.location.href = '/clientes'; // O usa navigate('/clientes') si usas react-router
                                        }
                                    }}
                                />
                            </DialogContent>
                        </Dialog>

                        {/* Modal para previsualización de PDFs */}
                        <Dialog 
                            open={openPdfPreview} 
                            onClose={handleClosePdfPreview}
                            fullScreen
                            PaperProps={{
                                style: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100vh',
                                    maxHeight: '100vh',
                                    margin: 0
                                }
                            }}
                        >
                            <DialogTitle>
                                {pdfTitle}
                            </DialogTitle>
                            
                            <DialogContent style={{ 
                                padding: 0, 
                                display: 'flex', 
                                flexDirection: 'column',
                                flex: 1,
                                overflow: 'hidden'
                            }}>
                                {pdfLoading && (
                                    <Box 
                                        display="flex" 
                                        flexDirection="column" 
                                        alignItems="center" 
                                        justifyContent="center" 
                                        flex={1}
                                        minHeight="50vh"
                                    >
                                        <CircularProgress size={60} />
                                        <Typography variant="body1" style={{ marginTop: 16 }}>
                                            Generando PDF...
                                        </Typography>
                                    </Box>
                                )}
                                
                                {pdfError && (
                                    <Box 
                                        p={3}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        flex={1}
                                        minHeight="50vh"
                                    >
                                        <Alert severity="error" style={{ maxWidth: '600px' }}>
                                            {pdfError}
                                        </Alert>
                                    </Box>
                                )}
                                
                                {pdfUrl && !pdfLoading && !pdfError && (
                                    <Box style={{ 
                                        flex: 1, 
                                        height: '100%',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        <iframe
                                            src={pdfUrl}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                border: 'none',
                                                flex: 1
                                            }}
                                            title="Vista previa del PDF"
                                        />
                                    </Box>
                                )}
                            </DialogContent>
                            
                            <DialogActions style={{ padding: '16px 24px' }}>
                                <Button 
                                    onClick={handleClosePdfPreview} 
                                    color="primary"
                                    variant="contained"
                                >
                                    Cerrar
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openDialog}
                            onClose={handleCloseDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Confirmar eliminación"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    ¿Estás seguro de que deseas eliminar el cliente seleccionado?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog} color="primary">Cancelar</Button>
                                <Button onClick={handleDeleteCliente} color="primary" autoFocus>Eliminar</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Modal para trasladar cliente */}
                        <Dialog
                            open={openTrasladarDialog}
                            onClose={handleCloseTrasladarDialog}
                            maxWidth="md"
                            fullWidth
                        >
                            <DialogTitle>
                                <Typography variant="h5" component="div">
                                    Trasladar Cliente
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Consolidar todos los datos de un cliente en otro registro
                                </Typography>
                            </DialogTitle>
                            <DialogContent dividers>
                                <Alert severity="warning" style={{ marginBottom: '20px' }}>
                                    <strong>⚠️ Acción irreversible:</strong> Todos los datos transaccionales del cliente origen 
                                    se trasladarán al cliente destino. El cliente origen será marcado como eliminado.
                                </Alert>

                                <Grid container spacing={3}>
                                    {/* Cliente Origen */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom color="error">
                                            Cliente Origen (Desde)
                                        </Typography>
                                        <Autocomplete
                                            options={clientesOrigenOpciones}
                                            getOptionLabel={(option) => 
                                                `${option.nombreCompleto} - ${option.numeroIdentidad}`
                                            }
                                            value={clienteOrigen}
                                            onChange={(event, newValue) => setClienteOrigen(newValue)}
                                            onInputChange={(event, newInputValue) => {
                                                setSearchClienteOrigen(newInputValue);
                                                handleBusquedaOrigenDebounced(newInputValue);
                                            }}
                                            loading={loadingBusqueda}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Buscar cliente origen"
                                                    placeholder="Escriba el nombre o número de identidad..."
                                                    helperText="Mínimo 3 caracteres. Presione Enter para buscar inmediatamente."
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            // Cancelar el debounce y buscar inmediatamente
                                                            if (debounceTimerOrigen.current) {
                                                                clearTimeout(debounceTimerOrigen.current);
                                                            }
                                                            buscarClientesParaTrasladar(searchClienteOrigen, 'origen');
                                                        }
                                                    }}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loadingBusqueda ? <CircularProgress size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.idCliente}>
                                                    <Box>
                                                        <Typography variant="body1">
                                                            <strong>{option.nombreCompleto}</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            ID: {option.numeroIdentidad} | Tel: {option.telefono || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            )}
                                            noOptionsText="No se encontraron clientes"
                                        />
                                        {clienteOrigen && (
                                            <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
                                                <Typography variant="body2">
                                                    <strong>Seleccionado:</strong> {clienteOrigen.nombreCompleto}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>ID:</strong> {clienteOrigen.numeroIdentidad}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Cliente ID:</strong> {clienteOrigen.idCliente}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>

                                    {/* Flecha indicadora */}
                                    <Grid item xs={12} style={{ textAlign: 'center' }}>
                                        <Typography variant="h4">⬇️</Typography>
                                        <Typography variant="body1" color="textSecondary">
                                            Los datos se trasladarán hacia:
                                        </Typography>
                                    </Grid>

                                    {/* Cliente Destino */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Cliente Destino (Hacia)
                                        </Typography>
                                        <Autocomplete
                                            options={clientesDestinoOpciones}
                                            getOptionLabel={(option) => 
                                                `${option.nombreCompleto} - ${option.numeroIdentidad}`
                                            }
                                            value={clienteDestino}
                                            onChange={(event, newValue) => setClienteDestino(newValue)}
                                            onInputChange={(event, newInputValue) => {
                                                setSearchClienteDestino(newInputValue);
                                                handleBusquedaDestinoDebounced(newInputValue);
                                            }}
                                            loading={loadingBusqueda}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Buscar cliente destino"
                                                    placeholder="Escriba el nombre o número de identidad..."
                                                    helperText="Mínimo 3 caracteres. Presione Enter para buscar inmediatamente."
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            // Cancelar el debounce y buscar inmediatamente
                                                            if (debounceTimerDestino.current) {
                                                                clearTimeout(debounceTimerDestino.current);
                                                            }
                                                            buscarClientesParaTrasladar(searchClienteDestino, 'destino');
                                                        }
                                                    }}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loadingBusqueda ? <CircularProgress size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.idCliente}>
                                                    <Box>
                                                        <Typography variant="body1">
                                                            <strong>{option.nombreCompleto}</strong>
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            ID: {option.numeroIdentidad} | Tel: {option.telefono || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            )}
                                            noOptionsText="No se encontraron clientes"
                                        />
                                        {clienteDestino && (
                                            <Box mt={2} p={2} bgcolor="primary.light" borderRadius={1}>
                                                <Typography variant="body2">
                                                    <strong>Seleccionado:</strong> {clienteDestino.nombreCompleto}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>ID:</strong> {clienteDestino.numeroIdentidad}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Cliente ID:</strong> {clienteDestino.idCliente}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>

                                {clienteOrigen && clienteDestino && clienteOrigen.idCliente === clienteDestino.idCliente && (
                                    <Alert severity="error" style={{ marginTop: '20px' }}>
                                        ❌ El cliente origen y destino no pueden ser el mismo
                                    </Alert>
                                )}
                            </DialogContent>
                            <DialogActions style={{ padding: '16px 24px' }}>
                                <Button onClick={handleCloseTrasladarDialog} color="primary">
                                    Cancelar
                                </Button>
                                <Button 
                                    onClick={handleTrasladarCliente} 
                                    color="secondary"
                                    variant="contained"
                                    disabled={!clienteOrigen || !clienteDestino || clienteOrigen?.idCliente === clienteDestino?.idCliente}
                                >
                                    Trasladar Cliente
                                </Button>
                            </DialogActions>
                        </Dialog>

                    </div>
                    {/* </div> */}
                    <br />


                    {/* <div className="col-md-7 col-sm-4 offset-md-2"> */}
                    <div ref={gestionClientesRef}>
                        <h2>Gestión de Clientes</h2>
                        <GestionClientes 
                            cliente={editCliente} 
                            onClienteGuardado={handleNewCliente} 
                            onRefreshClientesList={handleRefreshClientesList}
                        />
                    </div>
                    {/* </div> */}
                    {/* </div> */}
                </div>
            </div>
        </>
    );
}

export default Clientes;
